/**
 * Typed API client.
 *
 * Every backend error arrives in the single envelope from CLAUDE.md section 13, so
 * `ApiError` carries the `message_key` the UI localises rather than an English string
 * baked in by the server.
 */
import i18n from '@/i18n'

const API_BASE = '/api/v1'
const ACCESS_TOKEN_KEY = 'calcdesk.access_token'
const REFRESH_TOKEN_KEY = 'calcdesk.refresh_token'
const WORKSPACE_KEY = 'calcdesk.workspace_id'

export interface ApiErrorBody {
  code: string
  message_key: string
  message: string
  params: Record<string, unknown>
  detail?: unknown
}

export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    super(body.message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }

  /** Localised text for display. Falls back to the server's English message.
   *
   * Every backend `message_key` lives in the `errors` namespace, which a test on the
   * backend side asserts is complete (`tests/unit/test_i18n_coverage.py`). The other
   * namespaces are searched too so a key that legitimately lives elsewhere still
   * resolves rather than rendering raw.
   */
  get localised(): string {
    const key = this.body.message_key
    const translated = i18n.t(key, { ...this.body.params, ns: ['errors', 'auth', 'common'] })
    return translated === key ? this.body.message : translated
  }

  /** True for the load-shedding 429 the preview endpoint returns under pressure. */
  get isRateLimited(): boolean {
    return this.status === 429
  }

  /** Seconds the server asked us to wait, when it said. */
  get retryAfter(): number | null {
    const value = this.body.params?.retry_after
    return typeof value === 'number' ? value : null
  }
}

/* --- token storage -------------------------------------------------------------- */

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // A private window degrades to session-only auth rather than breaking.
  }
}

export const tokens = {
  get access() {
    return readStorage(ACCESS_TOKEN_KEY)
  },
  get refresh() {
    return readStorage(REFRESH_TOKEN_KEY)
  },
  set(access: string, refresh: string) {
    writeStorage(ACCESS_TOKEN_KEY, access)
    writeStorage(REFRESH_TOKEN_KEY, refresh)
  },
  clear() {
    writeStorage(ACCESS_TOKEN_KEY, null)
    writeStorage(REFRESH_TOKEN_KEY, null)
  },
}

export const activeWorkspace = {
  get id(): number | null {
    const raw = readStorage(WORKSPACE_KEY)
    return raw ? Number(raw) : null
  },
  set(id: number | null) {
    writeStorage(WORKSPACE_KEY, id === null ? null : String(id))
  },
}

/* --- request -------------------------------------------------------------------- */

interface RequestOptions {
  method?: string
  body?: unknown
  auth?: boolean
  signal?: AbortSignal
  /** Extra headers, e.g. `If-Match` for optimistic locking (CLAUDE.md 10.2). */
  headers?: Record<string, string>
}

let refreshInFlight: Promise<boolean> | null = null

/** Refresh once even if several requests 401 at the same time. */
async function refreshAccessToken(): Promise<boolean> {
  const refresh = tokens.refresh
  if (!refresh) return false

  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      })
      if (!response.ok) {
        tokens.clear()
        return false
      }
      const data = (await response.json()) as { access_token: string; refresh_token: string }
      tokens.set(data.access_token, data.refresh_token)
      return true
    } catch {
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, signal, headers: extraHeaders } = options

  // A FormData body is multipart: the browser sets the Content-Type itself, boundary
  // included, and setting it by hand would produce a body the server cannot parse.
  const multipart = typeof FormData !== 'undefined' && body instanceof FormData

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {
      ...(multipart ? {} : { 'Content-Type': 'application/json' }),
      ...extraHeaders,
    }
    if (auth && tokens.access) headers.Authorization = `Bearer ${tokens.access}`
    // Validated against real membership on every request -- the token never carries
    // workspace permissions (CLAUDE.md 11.7).
    const workspaceId = activeWorkspace.id
    if (workspaceId !== null) headers['X-Workspace-Id'] = String(workspaceId)

    return fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : multipart ? (body as FormData) : JSON.stringify(body),
      signal,
    })
  }

  let response = await send()

  if (response.status === 401 && auth && tokens.refresh) {
    if (await refreshAccessToken()) response = await send()
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload: unknown = text ? JSON.parse(text) : null

  if (!response.ok) {
    const envelope = (payload as { error?: ApiErrorBody } | null)?.error
    throw new ApiError(
      response.status,
      envelope ?? {
        code: 'unknown',
        message_key: 'error.unknown',
        message: `Request failed (${response.status})`,
        params: {},
      },
    )
  }

  return payload as T
}

export const api = {
  /** Escape hatch for a verb+body combination the helpers below do not cover. */
  request,
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: 'GET', signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, signal }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  /**
   * PATCH under optimistic locking. A mismatch returns 409 naming who changed the
   * row and when, so the UI can say *who* rather than just "conflict" (10.2).
   */
  patchWithMatch: <T>(path: string, body: unknown, rowVersion: number) =>
    request<T>(path, {
      method: 'PATCH',
      body,
      headers: { 'If-Match': String(rowVersion) },
    }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Multipart POST. The only body type the client sends that is not JSON. */
  upload: <T>(path: string, form: FormData, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body: form, signal }),
  /** Unauthenticated -- used for login, registration and public-mode preview. */
  publicPost: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>(path, { method: 'POST', body, auth: false, signal }),
}
